import React, { useState, useEffect } from 'react';
import { getSubmissionDetail, overrideScore, addTrainerNotes, getSubmissionPdfUrl, requeueSubmission, generateFeedbackSummary, getSession } from '../services/api';

const styles = {
  heading: { fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '8px' },
  backBtn: { background: 'none', border: 'none', color: '#9C3D97', cursor: 'pointer', fontSize: '14px', marginBottom: '16px', padding: 0 },
  downloadBtn: {
    padding: '10px 18px', background: '#9C3D97', color: '#fff', border: 'none',
    borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
  },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  scoreHeader: {
    display: 'flex', gap: '24px', padding: '20px', background: '#fff', borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px', alignItems: 'center',
  },
  scoreBig: { fontSize: '36px', fontWeight: '800' },
  scoreLabel: { fontSize: '12px', color: '#718096', textTransform: 'uppercase' },
  sectionTitle: { fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px', marginTop: '24px' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' },
  th: { padding: '10px 14px', textAlign: 'left', background: '#f7fafc', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase' },
  td: { padding: '10px 14px', borderTop: '1px solid #e2e8f0', fontSize: '13px', color: '#4a5568' },
  awarded: { color: '#38a169', fontWeight: '700' },
  notAwarded: { color: '#e53e3e', fontWeight: '700' },
  overrideBtn: { padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '3px', background: '#fff', fontSize: '11px', cursor: 'pointer' },
  justification: { fontSize: '12px', color: '#718096', maxWidth: '300px' },
  notesSection: { background: '#fff', padding: '24px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginTop: '24px' },
  textarea: { width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', minHeight: '100px', outline: 'none', resize: 'vertical', marginTop: '8px' },
  submitNotesBtn: { padding: '10px 20px', background: '#1a202c', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', marginTop: '12px', fontSize: '14px' },
  success: { background: '#c6f6d5', color: '#276749', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
  qualityCard: { background: '#fff', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px' },
  qualityCardOverridden: { background: '#fffaf0' },
  qualityName: { fontWeight: '600', fontSize: '15px', color: '#2d3748' },
  qualityLevel: { fontSize: '14px', color: '#9C3D97', fontWeight: '600' },
  qualityPoints: { fontSize: '20px', fontWeight: '700', color: '#1a202c' },
  overrideRow: {
    display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px',
    paddingTop: '12px', borderTop: '1px solid #e2e8f0',
  },
  overrideLabel: { fontSize: '12px', color: '#718096', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  overrideInput: {
    width: '64px', padding: '6px 8px', border: '2px solid #e2e8f0', borderRadius: '4px',
    fontSize: '14px', outline: 'none', textAlign: 'center',
  },
  overrideSaveBtn: {
    padding: '6px 12px', background: '#9C3D97', color: '#fff', border: 'none',
    borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
  overrideSaveBtnDisabled: { background: '#e2e8f0', color: '#a0aec0', cursor: 'not-allowed' },
  overrideMaxHint: { fontSize: '12px', color: '#a0aec0' },
  overrideBadge: {
    fontSize: '12px', color: '#dd6b20', fontWeight: '600', marginTop: '4px',
  },
  resetLink: {
    background: 'none', border: 'none', color: '#9C3D97', textDecoration: 'underline',
    cursor: 'pointer', fontSize: '12px', padding: 0, marginLeft: '4px',
  },
  infoBar: {
    display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center',
    padding: '16px 20px', background: '#f7fafc', borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '16px',
  },
  infoItem: { fontSize: '14px', color: '#4a5568' },
  infoLabel: { fontSize: '11px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' },
  infoValue: { fontSize: '15px', fontWeight: '600', color: '#1a202c' },
  pdfLink: { fontSize: '14px', fontWeight: '600', color: '#9C3D97', textDecoration: 'none', cursor: 'pointer' },
};

// Rating text colors aligned to the Monday board hues (#12079750290),
// darkened for readability since they color the large score/rating numbers on
// a white background (the pale badge backgrounds would be illegible as text).
const ratingColors = { E: '#6b46c1', EE: '#b7791f', ME: '#2f855a', NI: '#b83280', NS: '#718096' };

export default function SubmissionReviewPage({ submissionId, onBack }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [qualityInputs, setQualityInputs] = useState({});
  // Per-chart-item partial-credit inputs, keyed by item_id (#12233238500).
  const [itemInputs, setItemInputs] = useState({});
  const [showRequeueConfirm, setShowRequeueConfirm] = useState(false);
  const [requeueRunning, setRequeueRunning] = useState(false);
  // Overall AI Summary Review (Monday #12233728282)
  const [summaryGenerating, setSummaryGenerating] = useState(false);
  const [summaryCopied, setSummaryCopied] = useState(false);

  // Only show the Re-queue button to admins flagged developer=true.
  // (Currently Gregg, Nick, Denyse — gated server-side too.)
  const session = getSession() || {};
  const isDeveloper = session.developer === true;

  useEffect(() => {
    async function load() {
      try {
        const result = await getSubmissionDetail(submissionId);
        setData(result);
      } catch { /* handle error */ }
      finally { setLoading(false); }
    }
    load();
  }, [submissionId]);

  async function handleRequeue() {
    setRequeueRunning(true);
    setMessage('');
    try {
      const result = await requeueSubmission(submissionId);
      if (result.error) {
        setMessage(`Re-queue failed: ${result.error}`);
      } else {
        setMessage(result.message || 'Submission re-queued.');
        // Pull fresh row state so the page reflects the new status/rating.
        try {
          const updated = await getSubmissionDetail(submissionId);
          setData(updated);
        } catch { /* leave stale data — banner already conveys outcome */ }
      }
    } catch (e) {
      setMessage(`Re-queue failed: ${e.message || 'Network error.'}`);
    } finally {
      setRequeueRunning(false);
      setShowRequeueConfirm(false);
    }
  }

  async function handleOverrideItem(itemId, currentAwarded) {
    const result = await overrideScore(submissionId, [
      { type: 'chart_detail', item_id: itemId, awarded: !currentAwarded }
    ]);
    if (!result.error) {
      setMessage(`Override applied. New Grand Total: ${result.new_grand_total}% (${result.new_rating})`);
      const updated = await getSubmissionDetail(submissionId);
      setData(updated);
    }
  }

  async function handleSetItemPoints(itemId, maxPoints) {
    const raw = itemInputs[itemId];
    if (raw === undefined || raw === '') return;
    const pts = parseFloat(raw);
    if (Number.isNaN(pts) || pts < 0 || pts > maxPoints) {
      setMessage(`Points must be between 0 and ${maxPoints}.`);
      return;
    }
    const result = await overrideScore(submissionId, [
      { type: 'chart_detail', item_id: itemId, points_awarded: pts }
    ]);
    if (result.error) {
      setMessage(result.error);
      return;
    }
    setMessage(`Partial credit set. New Grand Total: ${result.new_grand_total}% (${result.new_rating})`);
    setItemInputs((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    const updated = await getSubmissionDetail(submissionId);
    setData(updated);
  }

  async function handleResetItem(itemId) {
    const result = await overrideScore(submissionId, [
      { type: 'chart_detail', item_id: itemId, reset: true }
    ]);
    if (result.error) {
      setMessage(result.error);
      return;
    }
    setMessage(`Reset to AI score. New Grand Total: ${result.new_grand_total}% (${result.new_rating})`);
    const updated = await getSubmissionDetail(submissionId);
    setData(updated);
  }

  async function handleOverrideQuality(categoryName, maxPoints) {
    const raw = qualityInputs[categoryName];
    if (raw === undefined || raw === '') return;
    const newPoints = parseInt(raw, 10);
    if (Number.isNaN(newPoints) || newPoints < 0 || newPoints > maxPoints) {
      setMessage(`Points must be an integer between 0 and ${maxPoints}.`);
      return;
    }
    const result = await overrideScore(submissionId, [
      { type: 'quality', category: categoryName, points_awarded: newPoints }
    ]);
    if (result.error) {
      setMessage(result.error);
      return;
    }
    setMessage(`Override applied. New Grand Total: ${result.new_grand_total}% (${result.new_rating})`);
    setQualityInputs((prev) => {
      const next = { ...prev };
      delete next[categoryName];
      return next;
    });
    const updated = await getSubmissionDetail(submissionId);
    setData(updated);
  }

  async function handleResetQuality(categoryName) {
    const result = await overrideScore(submissionId, [
      { type: 'quality', category: categoryName, reset: true }
    ]);
    if (result.error) {
      setMessage(result.error);
      return;
    }
    setMessage(`Reset to AI score. New Grand Total: ${result.new_grand_total}% (${result.new_rating})`);
    setQualityInputs((prev) => {
      const next = { ...prev };
      delete next[categoryName];
      return next;
    });
    const updated = await getSubmissionDetail(submissionId);
    setData(updated);
  }

  async function handleAddNotes() {
    if (!notes.trim()) return;
    const result = await addTrainerNotes(submissionId, notes.trim());
    if (!result.error) {
      setMessage('Trainer notes added.');
      setNotes('');
      const updated = await getSubmissionDetail(submissionId);
      setData(updated);
    }
  }

  async function handleGenerateSummary() {
    setSummaryGenerating(true);
    setMessage('');
    try {
      const result = await generateFeedbackSummary(submissionId);
      if (result.error) {
        setMessage(`Summary generation failed: ${result.error}`);
      } else {
        setMessage('Overall AI Summary Review generated.');
        setData((prev) => ({
          ...prev,
          feedback_summary: result.feedback_summary,
          feedback_summary_generated_at: result.feedback_summary_generated_at,
          feedback_summary_by: result.feedback_summary_by,
          feedback_summary_scope: result.feedback_summary_scope || '',
          feedback_summary_stale: false,
        }));
      }
    } catch (e) {
      setMessage(`Summary generation failed: ${e.message || 'Network error.'}`);
    } finally {
      setSummaryGenerating(false);
    }
  }

  async function handleCopySummary() {
    try {
      await navigator.clipboard.writeText(data.feedback_summary || '');
      setSummaryCopied(true);
      setTimeout(() => setSummaryCopied(false), 2000);
    } catch {
      setMessage('Copy failed - select the summary text and copy manually.');
    }
  }

  // The summary is stale when any score override was applied after it was
  // generated - the wording may no longer match the current scores. The
  // summary is shared across the evaluation day's charts, so the backend
  // also sets feedback_summary_stale on EVERY chart of the day when any of
  // them is overridden or deleted (Denyse, 2026-08-04) - check that flag
  // first, since this chart's own override timestamps can't see a sibling.
  function summaryIsStale() {
    if (!data || !data.feedback_summary || !data.feedback_summary_generated_at) return false;
    if (data.feedback_summary_stale) return true;
    const gen = new Date(data.feedback_summary_generated_at).getTime();
    if (Number.isNaN(gen)) return false;
    let latest = 0;
    (((data.chart_details || {}).sections) || []).forEach((s) => {
      (s.items || []).forEach((it) => {
        if (it.override && it.override.timestamp) {
          latest = Math.max(latest, new Date(it.override.timestamp).getTime() || 0);
        }
      });
    });
    (((data.chart_overall_quality || {}).categories) || []).forEach((c) => {
      if (c.override && c.override.timestamp) {
        latest = Math.max(latest, new Date(c.override.timestamp).getTime() || 0);
      }
    });
    return latest > gen;
  }

  function formatAZ(isoString) {
    if (!isoString) return '—';
    try {
      const d = new Date(isoString);
      return d.toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) + ' AZ';
    } catch { return isoString.split('T')[0]; }
  }

  function csvEscape(val) {
    if (val === null || val === undefined) return '';
    let str = String(val);
    // Formula-injection guard: a cell starting with = + - @ (or tab/CR) is
    // treated as a live formula by Excel/Sheets. Prefix with a single quote
    // so it is rendered as text.
    if (/^[=+\-@\t\r]/.test(str)) {
      str = "'" + str;
    }
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  }

  function sanitizeFilename(s) {
    return (s || 'unknown').replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '') || 'unknown';
  }

  function handleDownloadCsv() {
    if (!data) return;
    const scoring = data.scoring || {};
    const chartDetails = data.chart_details || {};
    const quality = data.chart_overall_quality || {};

    const vaName = data.va_name || '';
    const vaEmail = data.va_email || '';
    const dayName = data.day_name || '';
    const caseName = data.scenario_name || '';
    const dateSubmitted = formatAZ(data.submitted_at);
    const grandTotal = scoring.grand_total != null ? scoring.grand_total.toFixed(2) : '';
    const rating = scoring.rating || '';
    const weightedDetails = scoring.weighted_chart_details != null ? scoring.weighted_chart_details.toFixed(2) : '';
    const weightedQuality = scoring.weighted_overall_quality != null ? scoring.weighted_overall_quality.toFixed(2) : '';

    const rows = [];
    rows.push([
      'VA Name', 'VA Email', 'Day', 'Case', 'Date Submitted',
      'Grand Total', 'Rating', 'Weighted Details (75)', 'Weighted Quality (25)',
      'Type', 'Section/Category', 'Item ID', 'Item/Level',
      'Awarded', 'Points Awarded', 'Max Points', 'Justification', 'Details',
      'Grader Override', 'Overridden By', 'Override Direction',
    ]);

    (chartDetails.sections || []).forEach((section) => {
      (section.items || []).forEach((item) => {
        const override = item.override || null;
        let direction = '';
        if (override && override.new_points !== undefined && override.new_points !== null) {
          // Partial-credit override: direction from the numeric points - the
          // awarded flag stays false when 0 -> partial, so the boolean
          // comparison below would read "no change".
          const oldPts = override.old_points || 0;
          if (override.new_points > oldPts) direction = 'Raised';
          else if (override.new_points < oldPts) direction = 'Lowered';
        } else if (override && override.old_value !== undefined && override.old_value !== item.awarded) {
          direction = (!override.old_value && item.awarded) ? 'Raised' : 'Lowered';
        }
        rows.push([
          vaName, vaEmail, dayName, caseName, dateSubmitted,
          grandTotal, rating, weightedDetails, weightedQuality,
          'Chart Detail',
          section.name || '',
          item.item_id || '',
          item.item_text || '',
          item.awarded ? 'Yes' : 'No',
          item.points_awarded != null ? item.points_awarded : '',
          item.max_points != null ? item.max_points : '',
          item.justification || '',
          '',
          override ? 'Yes' : 'No',
          override ? (override.grader || '') : '',
          direction,
        ]);
      });
    });

    (quality.categories || []).forEach((cat) => {
      const detailsParts = [];
      if (cat.error_types_found && cat.error_types_found.length) {
        detailsParts.push('Error types: ' + cat.error_types_found.join(', '));
      }
      if (cat.violations && cat.violations.length) {
        detailsParts.push('Violations: ' + cat.violations.join(', '));
      }
      const override = cat.override || null;
      let qDirection = '';
      if (override && override.old_points != null && override.old_points !== cat.points_awarded) {
        qDirection = (cat.points_awarded > override.old_points) ? 'Raised' : 'Lowered';
      }
      rows.push([
        vaName, vaEmail, dayName, caseName, dateSubmitted,
        grandTotal, rating, weightedDetails, weightedQuality,
        'Overall Quality',
        cat.name || '',
        '',
        cat.selected_level || '',
        '',
        cat.points_awarded != null ? cat.points_awarded : '',
        cat.max_points != null ? cat.max_points : '',
        cat.justification || '',
        detailsParts.join('; '),
        override ? 'Yes' : 'No',
        override ? (override.grader || '') : '',
        qDirection,
      ]);
    });

    if (data.feedback_summary) {
      rows.push([
        vaName, vaEmail, dayName, caseName, dateSubmitted,
        grandTotal, rating, weightedDetails, weightedQuality,
        'Overall AI Summary Review',
        data.feedback_summary_by || 'AI',
        '',
        formatAZ(data.feedback_summary_generated_at),
        '', '', '',
        data.feedback_summary.replace(/\r?\n+/g, ' '),
        '',
        '', '', '',
      ]);
    }
    if (data.trainer_notes) {
      rows.push([
        vaName, vaEmail, dayName, caseName, dateSubmitted,
        grandTotal, rating, weightedDetails, weightedQuality,
        'Trainer Notes', 'AI Summary', '', '', '', '', '', data.trainer_notes, '',
        '', '', '',
      ]);
    }
    (data.trainer_notes_history || []).forEach((note) => {
      rows.push([
        vaName, vaEmail, dayName, caseName, dateSubmitted,
        grandTotal, rating, weightedDetails, weightedQuality,
        'Trainer Notes',
        note.author || 'Trainer',
        '',
        formatAZ(note.timestamp),
        '', '', '',
        note.note || '',
        '',
        '', '', '',
      ]);
    });

    const csvContent = rows.map((row) => row.map(csvEscape).join(',')).join('\r\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizeFilename(vaName)}_${sanitizeFilename(caseName)}_${submissionId}_graded.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  if (loading) return <div>Loading...</div>;
  if (!data) return <div>Submission not found.</div>;

  const scoring = data.scoring || {};
  const chartDetails = data.chart_details || {};
  const quality = data.chart_overall_quality || {};

  return (
    <div>
      <button style={styles.backBtn} onClick={onBack}>&larr; Back to Trainee Overview</button>
      <div style={styles.headerRow} id="review-top">
        <h1 style={styles.heading}>Submission Review</h1>
        <button style={styles.downloadBtn} onClick={handleDownloadCsv}>
          VA Submission Review CSV
        </button>
        {isDeveloper && ['failed', 'failed_permanent', 'pending'].includes(data.grading_status) && (
          <button
            onClick={() => setShowRequeueConfirm(true)}
            style={{
              padding: '8px 16px', background: '#c53030', color: '#fff',
              border: 'none', borderRadius: '6px', fontSize: '14px',
              fontWeight: '600', cursor: 'pointer',
            }}
            title={`Re-queue this ${data.grading_status} submission for grading`}
          >
            Re-queue Grading
          </button>
        )}
      </div>
      {message && <div style={styles.success}>{message}</div>}

      {/* VA Info Bar */}
      <div style={styles.infoBar}>
        <div>
          <div style={styles.infoLabel}>VA Name</div>
          <div style={styles.infoValue}>{data.va_name || '—'}</div>
        </div>
        <div>
          <div style={styles.infoLabel}>Email</div>
          <div style={styles.infoValue}>{data.va_email || '—'}</div>
        </div>
        <div>
          <div style={styles.infoLabel}>Day</div>
          <div style={styles.infoValue}>{data.day_name || '—'}</div>
        </div>
        <div>
          <div style={styles.infoLabel}>Case</div>
          <div style={styles.infoValue}>{data.scenario_name || '—'}</div>
        </div>
        <div>
          <div style={styles.infoLabel}>Submitted</div>
          <div style={styles.infoValue}>{formatAZ(data.submitted_at)}</div>
        </div>
        <div>
          <div style={styles.infoLabel}>Source Document</div>
          <div>
            <a style={styles.pdfLink} href={getSubmissionPdfUrl(submissionId)}
               target="_blank" rel="noopener noreferrer">
              View Submitted PDF ↗
            </a>
          </div>
        </div>
      </div>

      {/* Score Summary */}
      <div style={styles.scoreHeader}>
        <div>
          <div style={styles.scoreLabel}>Grand Total</div>
          <div style={{ ...styles.scoreBig, color: ratingColors[scoring.rating] || '#4a5568' }}>
            {scoring.grand_total?.toFixed(2)}%
          </div>
        </div>
        <div>
          <div style={styles.scoreLabel}>Rating</div>
          <div style={{ ...styles.scoreBig, color: ratingColors[scoring.rating] || '#4a5568' }}>
            {scoring.rating}
          </div>
        </div>
        <div>
          <div style={styles.scoreLabel}>Chart Details (75%)</div>
          <div style={{ fontSize: '20px', fontWeight: '700' }}>{scoring.weighted_chart_details?.toFixed(2)}</div>
        </div>
        <div>
          <div style={styles.scoreLabel}>Overall Quality (25%)</div>
          <div style={{ fontSize: '20px', fontWeight: '700' }}>{scoring.weighted_overall_quality?.toFixed(2)}</div>
        </div>
      </div>

      {/* Sticky trainee bar + section navigation. Freezes the trainee's name
          and score while scrolling (Monday #12786932449, Dominique Rosales)
          and jumps to any part of the chart (Monday #12917267559 +
          #12594640467). Sticks to the top of the scroll area from here down;
          jump targets carry scrollMarginTop so they land below this bar. */}
      {(() => {
        const chip = { padding: '4px 12px', background: '#faf5ff', color: '#9C3D97', border: '1px solid #e9d8fd', borderRadius: '999px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' };
        const goTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
        return (
          <div style={{ position: 'sticky', top: 0, zIndex: 40, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              {/* Back-nav in the floating bar (Monday #12960494349, Dominique
                  Rosales): same handler as the page-top back button. */}
              <button style={{ padding: '4px 12px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '999px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }} onClick={onBack}>&larr; Back to Trainee Overview</button>
              <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a202c' }}>{data.va_name || ''}</span>
              <span style={{ fontSize: '13px', color: '#718096' }}>{data.scenario_name || ''}</span>
              <span style={{ fontWeight: '700', fontSize: '15px', color: ratingColors[scoring.rating] || '#4a5568' }}>
                {scoring.grand_total?.toFixed(2)}% {scoring.rating}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: '#718096', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Go to:</span>
              {(chartDetails.sections || []).map((s) => (
                <button key={s.name} style={chip} onClick={() => goTo(`goto-section-${s.name}`)}>{s.name}</button>
              ))}
              <button style={chip} onClick={() => goTo('goto-quality')}>Quality</button>
              <button style={chip} onClick={() => goTo('goto-summary')}>AI Summary</button>
              <button style={chip} onClick={() => goTo('goto-notes')}>Notes</button>
              <button style={chip} onClick={() => goTo('review-top')}>Top &uarr;</button>
            </div>
          </div>
        );
      })()}

      {/* Chart Details */}
      <h2 style={styles.sectionTitle}>Chart Details</h2>
      {(chartDetails.sections || []).map((section) => (
        <div key={section.name} id={`goto-section-${section.name}`} style={{ scrollMarginTop: '64px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568', margin: '16px 0 8px' }}>
            {section.name} ({section.section_total}/{section.section_max})
          </h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Item</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Points</th>
                <th style={styles.th}>Justification</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(section.items || []).map((item) => {
                const isPartial = item.points_awarded > 0 && item.points_awarded < item.max_points;
                const rawInput = itemInputs[item.item_id];
                const parsedPts = rawInput === undefined || rawInput === '' ? NaN : parseFloat(rawInput);
                const ptsValid = !Number.isNaN(parsedPts) && parsedPts >= 0 && parsedPts <= item.max_points;
                return (
                <tr key={item.item_id} style={item.override ? { background: '#fffaf0' } : {}}>
                  <td style={styles.td}><code>{item.item_id}</code></td>
                  <td style={{ ...styles.td, maxWidth: '250px' }}>{item.item_text}</td>
                  <td style={styles.td}>
                    <span style={isPartial ? { color: '#975a16', fontWeight: '600' } : (item.awarded ? styles.awarded : styles.notAwarded)}>
                      {isPartial ? 'Partial' : (item.awarded ? 'Awarded' : 'Not Awarded')}
                    </span>
                  </td>
                  <td style={styles.td}>{item.points_awarded}/{item.max_points}</td>
                  <td style={styles.justification}>{item.justification}</td>
                  <td style={styles.td}>
                    {/* Once overridden, hide Award/Remove and the partial
                        input - "Reset to AI score" is the only way back
                        (Monday #12187973245 follow-up). */}
                    {!item.override && (
                      <>
                        <button style={styles.overrideBtn}
                          onClick={() => handleOverrideItem(item.item_id, item.awarded)}>
                          {item.awarded ? 'Remove' : 'Award'}
                        </button>
                        {/* Partial credit (Monday #12233238500): set an exact
                            point value between 0 and the item max. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px' }}>
                          <input
                            type="number"
                            min="0"
                            max={item.max_points}
                            step="0.5"
                            placeholder="pts"
                            value={rawInput !== undefined ? rawInput : ''}
                            onChange={(e) => setItemInputs((prev) => ({ ...prev, [item.item_id]: e.target.value }))}
                            style={{ width: '52px', padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '12px' }}
                          />
                          <button
                            style={{ ...styles.overrideBtn, opacity: ptsValid ? 1 : 0.4, cursor: ptsValid ? 'pointer' : 'not-allowed' }}
                            disabled={!ptsValid}
                            title={`Set partial credit (0 to ${item.max_points})`}
                            onClick={() => handleSetItemPoints(item.item_id, item.max_points)}
                          >
                            Set
                          </button>
                        </div>
                      </>
                    )}
                    {item.override && (
                      <div style={{ fontSize: '10px', color: '#dd6b20', marginTop: '4px' }}>
                        Overridden by {item.override.grader}
                        {item.override.timestamp ? ` on ${formatAZ(item.override.timestamp)}` : ''}
                        <button style={styles.resetLink} onClick={() => handleResetItem(item.item_id)}>
                          Reset to AI score
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {/* Chart Overall Quality */}
      <h2 style={{ ...styles.sectionTitle, scrollMarginTop: '64px' }} id="goto-quality">Chart Overall Quality</h2>
      {(quality.categories || []).map((cat) => {
        const maxPoints = cat.max_points != null ? cat.max_points : null;
        const hasOverride = !!cat.override;
        const inputValue = qualityInputs[cat.name] !== undefined
          ? qualityInputs[cat.name]
          : String(cat.points_awarded != null ? cat.points_awarded : '');
        const parsed = inputValue === '' ? NaN : parseInt(inputValue, 10);
        const inputValid = maxPoints != null && !Number.isNaN(parsed) && parsed >= 0 && parsed <= maxPoints;
        return (
          <div key={cat.name} style={{ ...styles.qualityCard, ...(hasOverride ? styles.qualityCardOverridden : {}) }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={styles.qualityName}>{cat.name}</div>
                <div style={styles.qualityLevel}>{cat.selected_level}</div>
              </div>
              <div style={styles.qualityPoints}>
                {cat.points_awarded}{maxPoints != null ? `/${maxPoints}` : ''} pts
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#718096', marginTop: '8px' }}>{cat.justification}</div>
            {cat.error_types_found && cat.error_types_found.length > 0 && (
              <div style={{ fontSize: '12px', color: '#e53e3e', marginTop: '4px' }}>
                Error types: {cat.error_types_found.join(', ')}
              </div>
            )}
            {cat.violations && cat.violations.length > 0 && (
              <div style={{ fontSize: '12px', color: '#dd6b20', marginTop: '4px' }}>
                Violations: {cat.violations.join(', ')}
              </div>
            )}

            {hasOverride && (
              <div style={styles.overrideBadge}>
                AI: {cat.override.old_points} pts &rarr; Overridden to {cat.points_awarded} pts
                {cat.override.grader ? ` (by ${cat.override.grader}` : ''}
                {cat.override.timestamp ? `, ${formatAZ(cat.override.timestamp)})` : cat.override.grader ? ')' : ''}
                <button style={styles.resetLink} onClick={() => handleResetQuality(cat.name)}>
                  Reset to AI score
                </button>
              </div>
            )}

            {maxPoints != null ? (
              <div style={styles.overrideRow}>
                <span style={styles.overrideLabel}>Override Points</span>
                <input
                  style={styles.overrideInput}
                  type="number"
                  min="0"
                  max={maxPoints}
                  step="1"
                  value={inputValue}
                  onChange={(e) => setQualityInputs((prev) => ({ ...prev, [cat.name]: e.target.value }))}
                />
                <span style={styles.overrideMaxHint}>/ {maxPoints} max</span>
                <button
                  style={{ ...styles.overrideSaveBtn, ...(inputValid && parsed !== cat.points_awarded ? {} : styles.overrideSaveBtnDisabled) }}
                  onClick={() => handleOverrideQuality(cat.name, maxPoints)}
                  disabled={!inputValid || parsed === cat.points_awarded}
                >
                  Save
                </button>
              </div>
            ) : (
              <div style={{ ...styles.overrideRow, color: '#a0aec0', fontSize: '12px', fontStyle: 'italic' }}>
                Max points unavailable - case config not found.
              </div>
            )}
          </div>
        );
      })}

      {/* Overall AI Summary Review (Monday #12233728282) — the primary
          overall submission review, trainee-facing, for scorecard paste.
          Trainer Notes below supplement it. */}
      {data.grading_status === 'complete' && !data.no_submission && (
        <div style={styles.notesSection}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <h2 style={{ ...styles.sectionTitle, marginTop: 0, marginBottom: 0, scrollMarginTop: '64px' }} id="goto-summary">Overall AI Summary Review</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              {data.feedback_summary && (
                <button
                  style={{ padding: '8px 14px', background: '#fff', color: '#9C3D97', border: '2px solid #9C3D97', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                  onClick={handleCopySummary}
                >
                  {summaryCopied ? 'Copied!' : 'Copy for Scorecard'}
                </button>
              )}
              <button
                style={{ padding: '8px 14px', background: summaryGenerating ? '#e2e8f0' : '#9C3D97', color: summaryGenerating ? '#a0aec0' : '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: summaryGenerating ? 'not-allowed' : 'pointer' }}
                onClick={handleGenerateSummary}
                disabled={summaryGenerating}
              >
                {summaryGenerating ? 'Generating...' : (data.feedback_summary ? 'Regenerate' : 'Generate Summary')}
              </button>
            </div>
          </div>
          {summaryIsStale() && (
            <div style={{ background: '#fffaf0', color: '#dd6b20', padding: '10px 12px', borderRadius: '6px', marginTop: '12px', fontSize: '13px', fontWeight: '600' }}>
              This summary may be outdated - a score was overridden or a chart was deleted on this evaluation day after it was generated. Click Regenerate so the feedback reflects the current state.
            </div>
          )}
          {data.feedback_summary ? (
            <>
              <div style={{ background: '#faf5ff', borderLeft: '4px solid #9C3D97', padding: '14px 16px', borderRadius: '6px', marginTop: '12px' }}>
                {data.feedback_summary.split(/\n\n+/).map((p, i) => (
                  <p key={i} style={{ margin: i === 0 ? 0 : '10px 0 0 0', fontSize: '14px', color: '#2d3748', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>
              <div style={{ fontSize: '12px', color: '#a0aec0', marginTop: '8px' }}>
                Generated {formatAZ(data.feedback_summary_generated_at)}{data.feedback_summary_by ? ` by ${data.feedback_summary_by}` : ''}
                {data.feedback_summary_scope ? <span style={{ color: '#805ad5', fontWeight: '600' }}> · {data.feedback_summary_scope}</span> : ''}
              </div>
            </>
          ) : (
            <p style={{ fontSize: '14px', color: '#718096', marginTop: '12px', marginBottom: 0 }}>
              No AI summary yet for this submission. Click Generate Summary to compose trainee-facing feedback from this evaluation. (New gradings generate this automatically.)
            </p>
          )}
        </div>
      )}

      {/* Trainer Notes */}
      <div style={styles.notesSection}>
        <h2 style={{ ...styles.sectionTitle, scrollMarginTop: '64px' }} id="goto-notes">Trainer Notes</h2>
        {data.trainer_notes && (
          <div style={{ background: '#f7fafc', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px', color: '#4a5568' }}>
            <strong>AI Notes:</strong> {data.trainer_notes}
          </div>
        )}
        {(data.trainer_notes_history || []).slice().reverse().map((note, i) => (
          <div key={i} style={{ background: '#fffaf0', padding: '12px', borderRadius: '6px', marginBottom: '8px', fontSize: '14px' }}>
            <strong>{note.author}</strong> ({formatAZ(note.timestamp)}): {note.note}
          </div>
        ))}
        <textarea style={styles.textarea} value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Add trainer notes..." />
        <button style={styles.submitNotesBtn} onClick={handleAddNotes}>Add Notes</button>
      </div>

      {/* Re-queue confirmation modal — developer-only */}
      {showRequeueConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 2000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '28px',
            width: '500px', maxWidth: '92vw', boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: 0, marginBottom: '12px', color: '#1a202c', fontSize: '18px' }}>
              Re-queue Grading
            </h3>
            <div style={{
              background: '#fffaf0', color: '#744210', padding: '12px 14px',
              borderRadius: '6px', fontSize: '14px', marginBottom: '14px',
              border: '1px solid #ecc94b', lineHeight: '1.5',
            }}>
              This submission is currently in <strong>{data.grading_status}</strong> state.
              <br /><br />
              The system will first check whether this row already has complete grading data
              (from a watchdog race) — if so, it will restore the original grade with no
              Claude API call. Otherwise, it will be sent to the grading worker for a fresh
              attempt (this uses Anthropic API credits).
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRequeueConfirm(false)}
                disabled={requeueRunning}
                style={{ padding: '8px 16px', background: '#edf2f7', border: 'none',
                         borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleRequeue}
                disabled={requeueRunning}
                style={{
                  padding: '8px 16px', background: '#c53030', color: '#fff',
                  border: 'none', borderRadius: '6px', fontSize: '14px',
                  fontWeight: '600', cursor: requeueRunning ? 'not-allowed' : 'pointer',
                  opacity: requeueRunning ? 0.6 : 1,
                }}
              >
                {requeueRunning ? 'Re-queueing...' : 'Re-queue Grading'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
